import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheckCircle,
  FiKey,
  FiLock,
  FiMail,
} from "react-icons/fi";

import api from "../services/api";

const steps = ["email", "otp", "password"];

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    email: "",
    otp: "",
    new_password1: "",
    new_password2: "",
  });

  const currentStep = steps[step];

  const inputBaseClass =
    "w-full h-12 rounded-2xl bg-white/12 border border-white/25 pl-12 pr-4 text-black caret-black placeholder:text-gray-500 font-medium outline-none backdrop-blur-md transition-all duration-300 focus:bg-white/18 focus:border-white/40 focus:shadow-[0_0_0_4px_rgba(255,255,255,0.06)]";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess("");
  };

  const stepMeta = useMemo(() => {
    if (currentStep === "email") {
      return {
        icon: <FiMail size={22} />,
        title: t("authPage.resetEmailStepTitle"),
        subtitle: t("authPage.resetEmailStepLead"),
        cta: t("authPage.sendOtp"),
      };
    }
    if (currentStep === "otp") {
      return {
        icon: <FiKey size={22} />,
        title: t("authPage.resetOtpStepTitle"),
        subtitle: t("authPage.resetOtpStepLead", { email: form.email.trim() }),
        cta: t("authPage.verifyOtp"),
      };
    }
    return {
      icon: <FiLock size={22} />,
      title: t("authPage.resetPasswordStepTitle"),
      subtitle: t("authPage.resetPasswordStepLead"),
      cta: t("authPage.updatePassword"),
    };
  }, [currentStep, form.email, t]);

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await api.post("/auth/password/reset/request/", {
        email: form.email.trim(),
      });
      setSuccess(t("authPage.resetOtpSent"));
      setStep(1);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          t("authPage.resetRequestFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await api.post("/auth/password/reset/verify-otp/", {
        email: form.email.trim(),
        otp: form.otp.trim(),
      });
      setStep(2);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          t("authPage.resetVerifyFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await api.post("/auth/password/reset/confirm/", {
        email: form.email.trim(),
        otp: form.otp.trim(),
        new_password1: form.new_password1,
        new_password2: form.new_password2,
      });
      navigate("/login", {
        replace: true,
        state: { passwordResetMessage: t("authPage.resetUpdated") },
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          t("authPage.resetConfirmFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (currentStep === "email") return requestOtp(e);
    if (currentStep === "otp") return verifyOtp(e);
    return submitReset(e);
  };

  const goBack = () => {
    if (step === 0) {
      navigate("/login");
      return;
    }
    setError("");
    setSuccess("");
    setStep((prev) => prev - 1);
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/login.png')" }}
    >
      <div className="absolute inset-0 bg-black/30" />
      <div className="absolute inset-y-0 left-0 w-[48%] bg-gradient-to-r from-black/35 via-black/10 to-transparent pointer-events-none" />

      <div className="relative z-10 min-h-screen flex">
        <aside className="w-full md:w-[32%] lg:w-[30%] xl:w-[28%] min-h-screen backdrop-blur-3xl bg-[linear-gradient(180deg,rgba(255,133,24,0.45)_0%,rgba(234,105,10,0.4)_55%,rgba(160,72,7,0.35)_100%)] border-r border-white/10 shadow-[20px_0_80px_rgba(0,0,0,0.28)]">
          <div className="h-full flex flex-col justify-between px-6 sm:px-8 lg:px-10 py-8">
            <div>
              <div className="flex justify-center md:justify-start mb-8 md:mb-10">
                <Link
                  to="/"
                  className="inline-flex items-center gap-3 hover:opacity-90 transition-opacity"
                >
                  <img
                    src="/logo.png"
                    alt="Chafadi"
                    className="h-9 md:h-10 w-auto object-contain"
                  />
                </Link>
              </div>

              <button
                type="button"
                onClick={goBack}
                className="mb-6 inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold transition-colors"
              >
                <FiArrowLeft size={16} />
                {step === 0 ? t("authPage.backToLogin") : t("common.back")}
              </button>

              <div className="mb-6 grid grid-cols-3 gap-2">
                {steps.map((name, index) => {
                  const active = index === step;
                  const done = index < step;
                  return (
                    <div
                      key={name}
                      className={`h-2 rounded-full transition-all ${
                        done || active ? "bg-white" : "bg-white/20"
                      }`}
                    />
                  );
                })}
              </div>

              <div className="mb-8 rounded-[28px] border border-white/14 bg-white/10 p-5 backdrop-blur-md">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/90 text-[#ff6b00]">
                  {stepMeta.icon}
                </div>
                <p className="text-[11px] font-extrabold tracking-[0.24em] uppercase text-white/55">
                  {`Step ${step + 1}`}
                </p>
                <h1 className="mt-2 text-white text-3xl sm:text-4xl lg:text-[42px] font-black tracking-tight leading-none">
                  {stepMeta.title}
                </h1>
                <p className="mt-3 text-white/78 text-[13px] md:text-sm leading-6">
                  {stepMeta.subtitle}
                </p>
              </div>

              {(error || success) && (
                <div
                  className={`mb-5 rounded-2xl border p-4 flex items-start gap-3 ${
                    error
                      ? "border-red-200/20 bg-red-500/15"
                      : "border-emerald-200/20 bg-emerald-500/15"
                  }`}
                >
                  <FiAlertCircle className="mt-0.5 shrink-0 text-white" size={18} />
                  <p className="text-sm text-white">{error || success}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {currentStep === "email" && (
                  <div className="relative">
                    <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" size={18} />
                    <input
                      type="email"
                      name="email"
                      placeholder={t("authPage.email")}
                      value={form.email}
                      onChange={handleChange}
                      className={inputBaseClass}
                      required
                    />
                  </div>
                )}

                {currentStep === "otp" && (
                  <>
                    <div className="rounded-2xl border border-white/14 bg-white/10 px-4 py-3 text-sm font-medium text-white/90">
                      {form.email.trim()}
                    </div>
                    <OtpBoxes value={form.otp} onChange={(value) => {
                      setForm((prev) => ({ ...prev, otp: value }));
                      setError("");
                      setSuccess("");
                    }} />
                    <div className="text-center pt-1">
                      <button
                        type="button"
                        onClick={requestOtp}
                        className="text-white/70 hover:text-white text-sm transition-colors"
                      >
                        {t("authPage.resendOtp")}
                      </button>
                    </div>
                  </>
                )}

                {currentStep === "password" && (
                  <>
                    <div className="relative">
                      <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" size={18} />
                      <input
                        type="password"
                        name="new_password1"
                        placeholder={t("authPage.newPassword")}
                        value={form.new_password1}
                        onChange={handleChange}
                        className={inputBaseClass}
                        required
                      />
                    </div>
                    <div className="relative">
                      <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" size={18} />
                      <input
                        type="password"
                        name="new_password2"
                        placeholder={t("authPage.confirmNewPassword")}
                        value={form.new_password2}
                        onChange={handleChange}
                        className={inputBaseClass}
                        required
                      />
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-2 h-11 rounded-2xl bg-gradient-to-b from-[#ffb21a] to-[#ff7a00] text-white font-extrabold text-base shadow-[0_12px_30px_rgba(255,122,0,0.35)] hover:translate-y-[-1px] hover:shadow-[0_16px_34px_rgba(255,122,0,0.42)] active:scale-[0.99] transition-all duration-300 flex items-center justify-center gap-2 border border-white/20"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/35 border-t-white rounded-full animate-spin" />
                  ) : (
                    stepMeta.cta
                  )}
                </button>
              </form>
            </div>

            <div className="pt-8">
              <p className="text-[11px] tracking-[0.24em] uppercase text-white/35 text-center md:text-left">
                Chafadi © 2026
              </p>
            </div>
          </div>
        </aside>

        <section className="hidden md:flex flex-1 min-h-screen relative">
          <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-black/0 to-black/10" />
          <div className="relative z-10 w-full h-full flex items-end justify-end p-10 lg:p-14">
            <div className="max-w-xl rounded-[32px] border border-white/10 bg-white/8 backdrop-blur-md p-6 shadow-[0_20px_80px_rgba(0,0,0,0.18)]">
              <div className="flex items-center gap-3 text-white mb-3">
                <FiCheckCircle size={18} />
                <span className="text-sm font-semibold">{t("authPage.resetSideTitle")}</span>
              </div>
              <p className="text-white/85 text-sm leading-7">
                {t("authPage.resetSideBody")}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const OtpBoxes = ({ value, onChange }) => {
  const boxes = Array.from({ length: 6 }, (_, index) => value[index] || "");
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-6 gap-2.5">
        {boxes.map((digit, index) => (
          <div
            key={index}
            className={`h-14 rounded-2xl flex items-center justify-center text-lg font-extrabold border transition-all ${
              digit
                ? "border-white bg-white/22 text-white"
                : "border-white/20 bg-white/8 text-white/65"
            }`}
          >
            {digit}
          </div>
        ))}
      </div>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="123456"
        className="w-full h-12 rounded-2xl bg-white/12 border border-white/25 px-4 text-black caret-black placeholder:text-gray-500 font-medium outline-none backdrop-blur-md transition-all duration-300 focus:bg-white/18 focus:border-white/40 focus:shadow-[0_0_0_4px_rgba(255,255,255,0.06)] text-center tracking-[0.45em]"
      />
    </div>
  );
};

export default ForgotPasswordPage;
